import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
// import IconButton from "@mui/material/IconButton";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
// import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
// import StopIcon from "@mui/icons-material/Stop";
// import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useEffect, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { getSupportedMimeTypes } from "../../utilities/mime";
import moment from "moment-timezone";

const SelfieVideo = ({
  active,
  onSave,
}: {
  active: boolean;
  onSave: (b: Blob) => void;
}) => {
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerInterval = useRef<any>(null);
  const [streaming, setStreaming] = useState(false);
  const [recordingRequest, setRecordingRequest] = useState(false);
  const [recorder, setRecorder] = useState<MediaRecorder>();
  const [showPreview, setShowPreview] = useState(false);
  const [blob, setBlob] = useState<Blob | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const preview = useRef<HTMLVideoElement>(null);
  const previewSrc = useRef<string>("");
  const recordedChunks = useRef<BlobPart[]>([]);
  const shouldRecord = useRef(false);
  const isRecording = useRef(false);
  const mimeType = useRef("");

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    (async () => {
      if (!active || streaming || !video.current) return;
      try {
        stream.current = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: 2,
            echoCancellation: false,
            sampleRate: 48000,
            sampleSize: 16,
          },
          video: {
            facingMode: "user",
            frameRate: 30,
            width: 1280,
          },
        });
        video.current.srcObject = stream.current;
        video.current.play();

        mimeType.current = getSupportedMimeTypes()[0];
        console.info("mimeType:", mimeType.current);
        const rec = new MediaRecorder(stream.current, {
          mimeType: mimeType.current,
          videoBitsPerSecond: 512000,
        });
        setRecorder(rec);

        setStreaming(true);
      } catch (e) {
        console.error(e);
        enqueueSnackbar("Problem obtaining MediaStream.", {
          variant: "error",
        });
      }
    })();
  }, [active, streaming, video, mimeType, stream, enqueueSnackbar]);

  useEffect(() => {
    if (!active && streaming && video.current) {
      setRecordingRequest(false);

      const tracks = (video.current.srcObject as any).getTracks();
      video.current.pause();
      video.current.src = "";
      tracks.forEach((s: any) => s.stop());
      setStreaming(false);
    }
  }, [active, streaming, video]);

  useEffect(() => {
    if (active) {
      setShowPreview(false);
    } else {
      setBlob(null);
      setElapsedTime(0);
      if (timerInterval.current) {
        clearInterval(timerInterval.current);
      }
    }
  }, [active]);

  useEffect(() => {
    if (!recorder || !recordedChunks.current) return;

    const onStart = () => {
      setBlob(null);
      // console.log("onStart");
      recordedChunks.current = [];
      isRecording.current = true;
      setElapsedTime(0);
      timerInterval.current = setInterval(() => {
        setElapsedTime((elapsed) => {
          return elapsed + 1;
        });
      }, 1000);
    };

    const dataAvailable = (evt: BlobEvent) => {
      // console.log("dataAvailable");
      if (evt.data.size > 0) {
        recordedChunks.current.push(evt.data);
      }

      // console.log(shouldRecord.current, isRecording.current);
      if (!shouldRecord.current && isRecording.current) {
        if (recorder.state !== "inactive") {
          recorder.stop();
          isRecording.current = false;
        }
      }
    };

    const onStop = () => {
      // console.log("onStop");
      const blob = new Blob(recordedChunks.current, { type: mimeType.current });
      // console.log(blob);
      setBlob(blob);

      video.current?.pause();

      if (!preview.current) return;
      if (previewSrc.current) URL.revokeObjectURL(previewSrc.current);
      previewSrc.current = URL.createObjectURL(blob);
      // console.log(previewSrc.current);
      preview.current.src = previewSrc.current;
      setShowPreview(true);
      clearInterval(timerInterval.current);
    };

    recorder.addEventListener("start", onStart);
    recorder.addEventListener("dataavailable", dataAvailable);
    recorder.addEventListener("stop", onStop);

    return () => {
      recorder.removeEventListener("start", onStart);
      recorder.removeEventListener("dataavailable", dataAvailable);
      recorder.removeEventListener("stop", onStop);
    };
  }, [
    recorder,
    recordedChunks,
    shouldRecord,
    isRecording,
    mimeType,
    video,
    preview,
    previewSrc,
  ]);

  useEffect(() => {
    if (!recorder) return;

    if (recordingRequest) {
      video.current?.play();
      setShowPreview(false);
      shouldRecord.current = true;
      recorder.start(1000);
    } else {
      shouldRecord.current = false;
    }
  }, [recorder, recordingRequest, shouldRecord, video]);

  return (
    <div className="flex-1 flex flex-col gap-4">
      <h2 className="font-head">RECORD VIDEO</h2>
      <video
        muted
        ref={video}
        className={`w-full ${showPreview ? "hidden" : ""}`}
      >
        Video stream not available.
      </video>
      <video
        ref={preview}
        className={`w-full ${showPreview ? "" : "hidden"}`}
        controls
        playsInline
      >
        Video stream not available.
      </video>
      <div className="flex gap-4">
        <ButtonGroup>
          <Button
            onClick={() => setRecordingRequest(true)}
            color="error"
            variant="outlined"
            disabled={recordingRequest}
          >
            Record
          </Button>
          <Button
            onClick={() => setRecordingRequest(false)}
            color="error"
            variant="outlined"
            disabled={!recordingRequest}
          >
            Stop
          </Button>
          <Button
            onClick={() => {
              if (blob) onSave(blob);
            }}
            color="error"
            variant="outlined"
            disabled={!blob}
          >
            Save
          </Button>
        </ButtonGroup>
        {recordingRequest && (
          <div>
            <AccessTimeIcon />
            {moment.utc(elapsedTime * 1000).format("HH:mm:ss")}
          </div>
        )}
      </div>
      {/* <div className="flex gap-4">
        <ButtonGroup variant="outlined">
          <IconButton
            onClick={() => setRecordingRequest(true)}
            color="error"
          >
            <FiberManualRecordIcon />
          </IconButton>
          <IconButton
            onClick={() => setRecordingRequest(false)}
            color="error"
          >
            <StopIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              if (blob) onSave(blob);
            }}
            color="error"
            disabled={!blob}
          >
            <UploadFileIcon />
          </IconButton>
        </ButtonGroup>
        { recordingRequest && <div>
          <AccessTimeIcon />
          { moment.utc(elapsedTime*1000).format("HH:mm:ss") }
        </div> }
      </div> */}
    </div>
  );
};

export default SelfieVideo;
